import type { SpatialFilter } from '@carto/api-client'
import type { SavedConfigV1 } from '@carto/kepler.gl'
import type { SourceFilters } from '@carto/react-redux'

import type { Modify } from 'features/common/utils/typeUtils'

import type { MapDataset } from '../types'
import type { LegendSettings } from './legend-types'
import type { KeplerSavedMapState, KeplerSavedVisState } from './kepler-types'
import type { WidgetConfig } from './widget-types'
import type { BasemapConfig, CustomBaseMapSettings } from './basemap-types'
import type { CartoMapLayer } from './layer-types'
import type { SqlParameter } from './sql-parameter-types'
import type { PopupSettings } from './popup-types'

export interface MapSettings {
  controlCacheTime: boolean

  /**
   * Dermines max-age of data loaded on initial map load.
   *
   * Only used if `controlCacheTime` and if value is non-zero.
   *
   * Unit: minutes.
   */
  cacheTime: number
  exportViewportData: boolean
  exportPDF: boolean
  comments: boolean
  addressSearchBar: boolean
  sqlParameterControls?: boolean
  scrollWheelZoom: boolean
  featureSelectionTool: boolean
  reorderLayers?: boolean
  showPerformanceWarnings: boolean
  showDateControl?: boolean
  showMyLocationButton: boolean
  showTitlePublicMap: boolean
  showMeasureDistanceTool: boolean
  basemapsSelector?: boolean
  measurementUnit?: 'miles' | 'kilometers'
}

export enum MapRightPaneTab {
  Widgets = 'widgets',
  Parameters = 'parameters'
}

export interface BuilderUiPersistenceState {
  // if map description pane is open
  descriptionOpen: boolean
  descriptionPreview: boolean

  // if controls (widgets & sql parameters) is expected to be open
  controlsPaneOpen: boolean

  // which pane (widgets or sql parameters) is currently active
  controlsPaneTab?: MapRightPaneTab

  // whether comments are visible in the map
  commentsVisible?: boolean
}

export enum NamedQueryParameters {
  From = 'start',
  To = 'end'
}

export interface DatesFilter {
  [NamedQueryParameters.From]: string | null
  [NamedQueryParameters.To]: string | null
  temporalColumn: string
}

// If in the future we add more filter, for example CategoryFilter change this
// type to Partial<DatesFilter & CategoryFilter>
export type QueryFilters = Partial<DatesFilter>

export type DynamicTilingMigrationBackup = {
  layers: CartoMapLayer[]
  popupSettings?: PopupSettings
  widgets?: WidgetConfig[]
  datasets: MapDataset[]
}

export interface AISettings {
  enabled: boolean
  mapContext: string
  conversationStarters: string[]
  userGuideEnabled: boolean
  userGuide: string
  /** After migrating {@link AISettings} to {@link Agent}, migration must not run again. [sc-505727] */
  migratedAt?: string
}

export type SavedVisState = Modify<KeplerSavedVisState, { layers: CartoMapLayer[] }>

export type CartoMapConfig = {
  // derived from Kepler
  mapState: KeplerSavedMapState

  /** @deprecated We now store basemap config in basemapConfig */
  mapStyle: Pick<SavedConfigV1['config']['mapStyle'], 'visibleLayerGroups' | 'styleType'>

  visState: SavedVisState

  // custom carto props
  basemapConfig?: BasemapConfig
  widgets?: WidgetConfig[]
  filters?: { [sourceId: string]: SourceFilters }
  spatialFilter?: { geometry: SpatialFilter } | null
  customBaseMaps?: CustomBaseMapSettings
  legendSettings?: LegendSettings
  mapSettings?: MapSettings
  popupSettings?: PopupSettings
  sourcesQueryFilters?: Record<string, QueryFilters> // depreacted
  sqlParameters?: SqlParameter[]
  aiSettings?: AISettings // deprecated, now stored in agent props of map object
  uiState?: BuilderUiPersistenceState
  dynamicTilingMigrationBackup?: DynamicTilingMigrationBackup
}

export type CartoMapConfigEnvelope = {
  version?: string
  config: CartoMapConfig
}

/** @deprecated Use CartoMapConfigEnvelope instead */
export type CartoKeplerMapConfigEnvelope = CartoMapConfigEnvelope
/** @deprecated Use CartoMapConfig instead */
export type CartoKeplerMapConfig = CartoMapConfig
